// One instance belongs to the customer bottom menu; no customer data is persisted.
export const useCustomerChatUnread = () => {
  const unreadCount = ref(0)
  const api = useChatApi()
  const session = useAuthSession()
  const authStore = useAuthStore()
  const { isAuthenticated } = useAuthDialog()
  let generation = 0
  let requestController
  let activeRequest = null
  let refreshAgain = false
  let timer
  let debounce
  let disposed = false
  const canRefresh = () => !disposed && isAuthenticated.value
    && document.visibilityState === 'visible' && navigator.onLine !== false

  const refresh = () => {
    if (!canRefresh()) return Promise.resolve()
    if (activeRequest) { refreshAgain = true; return activeRequest }
    const version = generation
    const controller = new AbortController()
    requestController = controller
    const task = (async () => {
      try {
        let cursor = null
        const conversations = new Map()
        do {
          const page = await api('/conversations', { signal: controller.signal, query: {
            limit: 100, ...(cursor ? { beforeActivity: cursor.lastActivity, beforeId: cursor.id } : {}),
          } })
          if (version !== generation || disposed) return
          for (const conversation of page.items) conversations.set(conversation.id, conversation)
          if (!page.hasMore) break
          const last = page.items.at(-1)
          // Do not loop forever if a server returns an invalid/repeated cursor.
          if (!last || (cursor && last.lastActivity === cursor.lastActivity && last.id === cursor.id)) return
          cursor = last
        } while (true)
        if (version !== generation || disposed || refreshAgain) return
        unreadCount.value = [...conversations.values()].reduce((total, conversation) => {
          const count = Number(conversation.unreadCount)
          return total + (Number.isSafeInteger(count) && count > 0 ? count : 0)
        }, 0)
      } catch {
        // Preserve the last confirmed count during a temporary network failure.
      }
    })()
    activeRequest = task
    void task.finally(() => {
      if (activeRequest !== task) return
      activeRequest = null
      if (refreshAgain) { refreshAgain = false; void refresh() }
    })
    return task
  }
  const scheduleRefresh = () => {
    clearTimeout(debounce)
    debounce = setTimeout(() => { void refresh() }, 250)
  }
  const realtime = useChatRealtime(scheduleRefresh)
  const reset = () => {
    generation++
    requestController?.abort()
    activeRequest = null
    refreshAgain = false
    unreadCount.value = 0
    clearTimeout(debounce)
    realtime.stop()
    if (isAuthenticated.value && !disposed) {
      void refresh()
      void realtime.start().catch(() => {})
    }
  }
  let stopAuthWatch
  onMounted(() => {
    session.read()
    stopAuthWatch = watch([isAuthenticated, () => authStore.ownerPhoneNumber], reset, { immediate: true })
    timer = setInterval(() => { void refresh() }, 30000)
    window.addEventListener('online', scheduleRefresh)
    window.addEventListener('focus', scheduleRefresh)
    window.addEventListener('alomonshi:customer-chat-read', scheduleRefresh)
    document.addEventListener('visibilitychange', scheduleRefresh)
  })
  onBeforeUnmount(() => {
    disposed = true
    stopAuthWatch?.()
    reset()
    clearInterval(timer)
    window.removeEventListener('online', scheduleRefresh)
    window.removeEventListener('focus', scheduleRefresh)
    window.removeEventListener('alomonshi:customer-chat-read', scheduleRefresh)
    document.removeEventListener('visibilitychange', scheduleRefresh)
  })
  return { unreadCount }
}
